import classNames from "classnames";
import { add, lessThanOrEqual } from "dinero.js";
import React from "react";
import { Image } from "tsi-common-react/src/components/Image/index";
import { Link } from "tsi-common-react/src/components/Link/index";
import { RatingGraphic } from "tsi-common-react/src/components/RatingGraphic/index";
import { AddToCartType } from "tsi-common-react/src/constants";
import { IProduct } from "tsi-common-react/src/models/catalogue";
import { isoMonths } from "tsi-common-react/src/models/nominals";
import { trackAddToBasketEvent } from "tsi-common-react/src/utils/analytics";
import { listFinancingPlans } from "tsi-common-react/src/utils/financing";
import { format } from "tsi-common-react/src/utils/format";
import {
    type Dinero,
    getDinero,
    getMonthlyPrice,
} from "tsi-common-react/src/utils/money";
import { usePageSetting } from "tsi-common-react/src/utils/settings";
import { jt, t } from "ttag";

import { constants } from "@reactivated/constants";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import { IBasketLineSuggestionProps } from "../BasketLineSuggestion.interfaces";

const AppSlug = constants["tsicommon.types.AppSlug"];

const scrollToFinePrint = (event: React.MouseEvent<HTMLElement>) => {
    if (event) {
        event.preventDefault();
    }

    const document = getDocument();
    const finePrint = document?.querySelector("#basket-lines__fine-print");
    finePrint?.scrollIntoView({ behavior: "smooth" });
};

const buildImage = (product: IProduct) => {
    // Use the product image if one exists else try the parent image
    let image = product.images.slice().shift();
    if (!image && product.parent) {
        image = product.parent.images.slice().shift();
    }
    return image ? (
        <Link
            href={product.parent?.link || product.link}
            title={t`Learn more about ${product.title}`}
        >
            <Image
                alt={product.title}
                src={image.original}
                className="basket-line__graphic__image"
            />
        </Link>
    ) : null;
};

const buildOptions = (product: IProduct) => {
    const attributes = product.attributes;
    const optionKeys = attributes.product_options?.value || [];
    if (optionKeys.length <= 0) {
        return null;
    }

    const options = optionKeys.map((key) => {
        const attr = attributes[key];
        if (!attr) {
            return null;
        }
        return (
            <li key={key} className="basket-suggestion__details__option">
                {(() => {
                    const name = <span>{attr.name}</span>;
                    const value = <strong>{attr.value}</strong>;
                    return jt`${name}: ${value}`;
                })()}
            </li>
        );
    });

    return (
        <div className="basket-suggestion__details__options">
            <ul>{options}</ul>
        </div>
    );
};

const calcPlanMonths = (total: Dinero) => {
    const plans = listFinancingPlans();
    const availablePlans = plans.filter((plan) => {
        return lessThanOrEqual(plan.threshold, total);
    });
    const bestPlan = availablePlans[availablePlans.length - 1];
    return bestPlan ? isoMonths.unwrap(bestPlan.length) : null;
};

const buildStarRating = (product: IProduct) => {
    const rating = product.rating;
    const cardType = "cart";
    const numReviews = 5;
    const ratingNum = (
        <RatingGraphic
            rating={rating}
            numReviews={numReviews}
            onlyReviewsNum={false}
            cardClass={cardType}
            cardSize={"small"}
        />
    );
    return ratingNum;
};

const buildAddToBasketButton = (product: IProduct, addToBasket: () => void) => {
    let addToCartLabel: string = t`Add to cart`;
    let isPreorder = false;
    if (
        product.attributes &&
        product.attributes.preorder &&
        product.attributes.preorder.value
    ) {
        addToCartLabel = t`Pre-Order`;
        isPreorder = true;
    }

    const buttonClasses = classNames({
        "button": true,
        "button--secondary": isPreorder,
    });

    return (
        <button className={buttonClasses} onClick={addToBasket}>
            {addToCartLabel}
        </button>
    );
};

export const BasketLineSuggestionB = (
    props: IBasketLineSuggestionProps,
): React.ReactNode => {
    const appSlug = usePageSetting("app_slug") || "";
    const product = props.product;
    const rootProduct = product.parent || product;

    const addToBasket = async (): Promise<void> => {
        await props.addBasketLine(props.product.url, 1);
        trackAddToBasketEvent(
            props.product,
            1,
            AddToCartType.IN_CART_UP_CROSS_SELL,
        );
    };

    let financing: React.JSX.Element | null = null;
    if (product.price.enable_per_month_pricing) {
        // Calculate Suggested plan based on (Cart total + Suggestion Product total) / (Highest plan that total qualifies for)
        const basketTotalPrice = getDinero(props.basketTotal);
        const extraProductPrice = getDinero(
            props.product.price.cosmetic_excl_tax || "0.00",
        );
        const computedTotalPrice = add(basketTotalPrice, extraProductPrice);
        const planMonths = calcPlanMonths(computedTotalPrice);
        if (planMonths) {
            const computedMonthlyPrice = getMonthlyPrice(
                getDinero(props.product.price.cosmetic_excl_tax || "0.00"),
                planMonths,
            );
            financing = (
                <p>
                    <button
                        onClick={scrollToFinePrint}
                        className="basket-suggestion__financing-button"
                    >
                        {(() => {
                            const monthlyPrice = (
                                <span
                                    key="price"
                                    className="basket-suggestion__financing--underline"
                                >
                                    {format.money(computedMonthlyPrice)}
                                </span>
                            );
                            const months = (
                                <span
                                    key="months"
                                    className="basket-suggestion__financing--underline"
                                >
                                    {planMonths}
                                </span>
                            );
                            const sup1 = <sup key="sup1">1</sup>;
                            const sup2 = <sup key="sup2">2</sup>;
                            return jt`or ${monthlyPrice}/mo.${sup2} for ${months} months${sup1}`;
                        })()}
                    </button>
                </p>
            );
        }
    }

    const isCanada = appSlug === AppSlug.TEMPURCA;

    return (
        <div
            role="region"
            aria-label={t`Upsell Carousel`}
            className="basket-suggestion basket-suggestion--version-b"
        >
            <div className="basket-suggestion__row basket-suggestion__row--version-b">
                <h4>
                    {richT(
                        t`Complete Your Tempur-Pedic <sup>®</sup> Sleep Experience`,
                    )}
                </h4>
            </div>
            <div className="basket-suggestion__row">
                <div className="basket-suggestion__graphic">
                    {buildImage(product)}
                </div>
                <div className="basket-suggestion__details basket-suggestion__details--version-b">
                    <h4 className="basket-suggestion__details-title">
                        <Link href={rootProduct.link}>{rootProduct.title}</Link>
                    </h4>
                    <div className="basket-suggestion__details__tag">
                        {t`Best Seller`}
                    </div>
                    {!isCanada && buildStarRating(product)}
                    {buildOptions(product)}
                </div>
                <div className="basket-suggestion__price basket-suggestion__price--version-b">
                    <p>{format.money(product.price.cosmetic_excl_tax)}</p>
                    <div className="basket-suggestion__financing">
                        {financing}
                    </div>
                    {buildAddToBasketButton(product, addToBasket)}
                </div>
            </div>
        </div>
    );
};
